<script setup>
// O que o agente PERCEBE do cliente: nota de voz virando texto, e imagem que ele olha.
//
// Aba própria porque os interruptores existiam e eram inalcançáveis: moravam no `MediaSection`,
// que NÃO estava na lista de seções — switch dentro de um componente que ninguém conseguia
// abrir. Aqui eles têm endereço. O `MediaSection` foi apagado junto com a chegada do segundo
// switch: arquivo órfão que escreve chave viva não é curiosidade histórica, é armadilha —
// quem fosse implementar anexo o acharia, o editaria, e a tela não mudaria.
//
// Três decisões visíveis:
//
//   1. nasce LIGADO: só o `transcribe_audio: false` explícito desliga (02/10/2026, pedido do
//      Murilo — transcrição por padrão para todos os agentes). Até ali nascia desligado porque
//      transcrever custa a cada nota de voz. A regra é a de `helpers/transcricao.js`, a mesma
//      do servidor, e a tela mostra ligado o agente que nunca gravou a chave porque é o que
//      ele faz;
//   2. a chave é POR AGENTE e vale para os três provedores, inclusive a OpenRouter. Sem chave
//      própria, a OpenRouter cai na chave da casa; Groq e AssemblyAI não têm chave nossa, e
//      por isso lá a ausência é erro nomeado em vez de cobrança nossa por engano;
//   3. a chave é PROVADA antes de valer, mandando um áudio de um segundo. Não é consulta de
//      saldo: só a OpenRouter publica saldo, e um selo verde que significasse "a chave existe"
//      num provedor e "a chave tem crédito" no outro seria mentira em dois terços dos casos.
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import { transcreveAudio } from '../helpers/transcricao';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Button from 'dashboard/components-next/button/Button.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');

const PROVEDORES = ['openrouter', 'groq', 'mistral', 'assemblyai'];

const config = props.agent.config || {};
const ligado = ref(transcreveAudio(config));
// Agente novo nasce com isto LIGADO, como a dica da tela diz: quem grava
// `read_attachments: true` é a criação no servidor (`create_params` do
// `PowerAgentsController`), porque quem cria um agente espera que ele entenda o que o cliente
// manda. O `?? false` é do agente de antes disso, que nunca teve a chave: o runtime lê a
// ausência como desligado, e a tela mostra o mesmo. Desligado, o agente ainda SABE que o
// arquivo chegou e diz isso — o que ele não faz é subir os bytes ao modelo.
const lerAnexos = ref(config.read_attachments ?? false);
// Rajada: um celular mandando 80 fotos cria 80 mensagens, e sem estas três chaves o agente
// responde 80 vezes. Ficam AQUI, e não numa aba própria, porque são a mesma pergunta que o
// resto da seção responde — o que o agente percebe do cliente e o que ele faz com isso.
//
// As três nascem no valor que preserva o comportamento antigo. Nenhuma delas muda nada
// sozinha: o zero da carência é "um turno por mensagem", como sempre foi.
const carencia = ref(config.attachment_debounce_seconds ?? 0);
const tetoRajada = ref(config.attachment_burst_max ?? 60);
const exigeTexto = ref(config.attachment_needs_text ?? false);
const provedor = ref(config.transcription_provider || 'openrouter');
const chave = ref('');
const saving = ref(false);
const validando = ref(false);
// `null` = ninguém testou ainda. É diferente de "testou e falhou", e a tela não pode mostrar
// as duas do mesmo jeito: uma pede um teste, a outra pede uma chave nova.
const veredito = ref(null);

// Só a OpenRouter tem chave da casa. Nos outros dois, sem chave própria não há transcrição —
// e dizer isso na hora evita o cliente ligar a feature e descobrir pelo silêncio.
const exigeChave = computed(() => provedor.value !== 'openrouter');
const temChaveGravada = computed(
  () =>
    (props.agent.secrets_configured || {})[`${provedor.value}_api_key`] != null
);

async function validar() {
  validando.value = true;
  veredito.value = null;
  try {
    veredito.value = await powerAgentsApi.secretsValidate(
      ctx.accountId(),
      ctx.agentId(),
      { provider: provedor.value, key: chave.value }
    );
  } catch (e) {
    veredito.value = { ok: false, reason: 'erro' };
  } finally {
    validando.value = false;
  }
}

// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  ligado: ligado.value,
  lerAnexos: lerAnexos.value,
  carencia: carencia.value,
  tetoRajada: tetoRajada.value,
  exigeTexto: exigeTexto.value,
  provedor: provedor.value,
  chave: chave.value,
}));

async function save() {
  saving.value = true;
  try {
    // A chave vai por `secretsUpdate`, que é o caminho cifrado; ela NUNCA entra no `config`,
    // que viaja inteiro para o runtime dentro do `publish_payload`.
    if (chave.value.trim()) {
      await powerAgentsApi.secretsUpdate(ctx.accountId(), ctx.agentId(), {
        secrets: { [`${provedor.value}_api_key`]: chave.value.trim() },
      });
      chave.value = '';
    }
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          ...(props.agent.config || {}),
          transcribe_audio: ligado.value,
          transcription_provider: provedor.value,
          read_attachments: lerAnexos.value,
          // `Number(...) || 0` e não `Number(...)`: input vazio devolve NaN, e NaN vira
          // `null` no JSON — o runtime leria "sem config" em vez do zero que a pessoa
          // deixou na tela, o que é a mesma coisa por acidente e não por decisão.
          attachment_debounce_seconds: Number(carencia.value) || 0,
          attachment_burst_max: Number(tetoRajada.value) || 60,
          attachment_needs_text: exigeTexto.value,
        },
      }
    );
    ctx.onSectionSaved(updated);
    // Transcrição muda o que o agente RECEBE, e isso só passa a valer na revisão publicada.
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);
</script>

<template>
  <SectionShell
    data-busca="audio"
    :title="t('POWER_AGENTS.AUDIO.TITLE')"
    :description="t('POWER_AGENTS.AUDIO.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <div data-busca="audio-enabled" class="flex items-start gap-3 p-1">
      <Switch v-model="ligado" class="mt-0.5" />
      <div class="min-w-0">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.ENABLED') }}
        </span>
        <span class="block mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.ENABLED_HINT') }}
        </span>
      </div>
    </div>

    <div data-busca="audio-anexos" class="flex items-start gap-3 p-1">
      <Switch v-model="lerAnexos" class="mt-0.5" />
      <div class="min-w-0">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.ATTACHMENTS') }}
        </span>
        <span class="block mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.ATTACHMENTS_HINT') }}
        </span>
      </div>
    </div>

    <div data-busca="audio-rajada" class="flex flex-col gap-3 pt-1">
      <div class="min-w-0">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.BURST_TITLE') }}
        </span>
        <span class="block mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.BURST_HINT') }}
        </span>
      </div>

      <div class="flex items-start gap-3 p-1">
        <Switch v-model="exigeTexto" class="mt-0.5" />
        <div class="min-w-0">
          <span class="block text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.AUDIO.NEEDS_TEXT') }}
          </span>
          <span class="block mt-0.5 text-xs text-n-slate-10">
            {{ t('POWER_AGENTS.AUDIO.NEEDS_TEXT_HINT') }}
          </span>
        </div>
      </div>

      <!-- A carência não tem o que fazer quando foto sozinha já não abre turno: os dois
           controles responderiam à mesma pergunta, e um deles sem efeito nenhum. -->
      <div v-if="!exigeTexto">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.DEBOUNCE') }}
        </label>
        <input
          v-model="carencia"
          type="number"
          min="0"
          max="120"
          class="reset-base w-32 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.DEBOUNCE_HINT') }}
        </p>
      </div>

      <div>
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.BURST_MAX') }}
        </label>
        <input
          v-model="tetoRajada"
          type="number"
          min="1"
          max="300"
          class="reset-base w-32 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.BURST_MAX_HINT') }}
        </p>
      </div>
    </div>

    <div v-if="ligado" class="flex flex-col gap-4">
      <div data-busca="audio-provider">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.PROVIDER') }}
        </label>
        <select
          v-model="provedor"
          class="w-full mb-0 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-alpha-1 text-n-slate-12 focus:border-n-brand"
        >
          <option v-for="p in PROVEDORES" :key="p" :value="p">
            {{ t(`POWER_AGENTS.AUDIO.PROVIDERS.${p.toUpperCase()}`) }}
          </option>
        </select>
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.PROVIDER_HINT') }}
        </p>
      </div>

      <div data-busca="audio-key">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.KEY') }}
        </label>
        <div class="flex flex-wrap items-center gap-2 sm:flex-nowrap">
          <input
            v-model="chave"
            type="password"
            autocomplete="off"
            class="reset-base flex-1 min-w-0 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
            :placeholder="
              temChaveGravada
                ? t('POWER_AGENTS.AUDIO.KEY_STORED')
                : t('POWER_AGENTS.AUDIO.KEY_PLACEHOLDER')
            "
          />
          <Button
            variant="faded"
            color="slate"
            size="sm"
            class="shrink-0"
            :is-loading="validando"
            :disabled="!chave.trim() && !temChaveGravada"
            :label="t('POWER_AGENTS.AUDIO.VALIDATE')"
            @click="validar"
          />
        </div>

        <!-- O veredito diz QUAL é o problema, porque a diferença muda o que a pessoa faz:
             chave errada se resolve recolando; conta sem saldo, não. -->
        <p v-if="veredito && veredito.ok" class="mt-2 text-xs text-n-teal-11">
          {{ t('POWER_AGENTS.AUDIO.VALID') }}
          <template v-if="veredito.balance && veredito.balance.usado != null">
            {{
              t('POWER_AGENTS.AUDIO.BALANCE', {
                usado: Number(veredito.balance.usado).toFixed(2),
              })
            }}
          </template>
        </p>
        <p v-else-if="veredito" class="mt-2 text-xs text-n-ruby-11">
          {{
            t(
              `POWER_AGENTS.AUDIO.INVALID.${String(veredito.reason).toUpperCase()}`
            )
          }}
        </p>

        <p
          v-if="exigeChave && !temChaveGravada"
          class="mt-2 text-xs text-n-amber-11"
        >
          {{ t('POWER_AGENTS.AUDIO.KEY_REQUIRED') }}
        </p>
        <p v-else class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.AUDIO.KEY_HINT') }}
        </p>
      </div>

      <!-- O que o agente vai ler. Está na tela porque quem configura precisa saber que a
           mensagem chega marcada, senão escreve um prompt que briga com o marcador. -->
      <div class="p-3 text-xs border rounded-lg border-n-weak text-n-slate-11">
        <p class="font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.AUDIO.AGENT_SEES') }}
        </p>
        <code class="block mt-1 font-mono break-all">
          {{ t('POWER_AGENTS.AUDIO.AGENT_SEES_EXAMPLE') }}
        </code>
        <p class="mt-2">{{ t('POWER_AGENTS.AUDIO.AGENT_SEES_HINT') }}</p>
      </div>
    </div>
  </SectionShell>
</template>
